/**
 * Service de gestion des transactions
 * Permet de créer, lire, mettre à jour et supprimer des transactions
 */

import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';

// Interface de transaction
export interface Transaction {
  id?: string; // Optional for new transactions
  date: string;
  categorie_id: string;
  description: string; // Champ obligatoire
  montant: number;
  compte_id: string;
  compte_destination_id?: string;
  type?: 'depense' | 'revenu' | 'transfert' | 'pret' | 'remboursement';
  user_id?: string;
  transaction_periodique_id?: string; // Référence à la transaction périodique source
  /** Chemin Storage (bucket transaction-attachments), option A client */
  attachment_path?: string | null;
}

// Interface enrichie pour les transactions avec détails de jointure
export interface TransactionWithDetails extends Transaction {
  categorie?: {
    id: string;
    nom: string;
    couleur: string;
    icone?: string | null;
    type: 'depense' | 'revenu';
  } | null;
  compte?: {
    id: string;
    nom: string;
    couleur: string;
  } | null;
  compte_destination?: {
    id: string;
    nom: string;
    couleur: string;
  } | null;
}

// Interface pour les filtres de transactions
export interface TransactionFilters {
  compte_id?: string;
  categorie_id?: string;
  month?: number;  // 0-11 (janvier = 0, décembre = 11)
  year?: number;
  searchQuery?: string;
  startDate?: string;
  endDate?: string;
}

// Interface pour les filtres de recherche globale
export interface GlobalSearchFilters {
  searchQuery?: string;
  type?: 'all' | 'depense' | 'revenu' | 'transfert' | 'pret' | 'remboursement';
  compte_id?: string;
  categorie_id?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// Interface pour le résultat de recherche globale
export interface GlobalSearchResult {
  transactions: TransactionWithDetails[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

/**
 * Crée une nouvelle transaction
 */
export const createTransaction = async (transaction: Transaction): Promise<{ data: Transaction | null; error: any }> => {
  try {
    // Récupérer l'ID de l'utilisateur connecté
    const { data: { user } } = await supabase.auth.getUser();
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    
    if (!user) {
      return { data: null, error: 'Utilisateur non connecté' };
    }

    // Créer la transaction avec l'ID de l'utilisateur (impersonation prioritaire)
    const { data, error } = await supabase
      .from('transactions')
      .insert([
        {
          ...transaction,
          user_id: impersonatedUserId || user.id,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création de la transaction:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la création de la transaction:', error);
    return { data: null, error };
  }
};

/**
 * Récupère les transactions filtrées
 */
export const getFilteredTransactions = async (
  filters: TransactionFilters
): Promise<{ data: TransactionWithDetails[] | null; error: any }> => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId || sessionData.session.user.id;

    // Récupération des transactions
    let transactionsData: any[] | null = null;
    if (impersonatedUserId) {
      // Utiliser RPC admin pour contourner RLS et avoir les jointures côté JSON
      const rpc = await supabase.rpc('admin_get_transactions', { p_user_id: userId });
      if (rpc.error) {
        console.error('Erreur RPC admin_get_transactions:', rpc.error);
        return { data: null, error: rpc.error };
      }
      transactionsData = (rpc.data as any[]) || [];
    } else {
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId);

    // Filtre par compte
    if (filters.compte_id) {
      query = query.eq('compte_id', filters.compte_id);
    }

    // Filtre par catégorie
    if (filters.categorie_id) {
      query = query.eq('categorie_id', filters.categorie_id);
    }

    // Filtre par période (mois/année)
    if (filters.month !== undefined && filters.year !== undefined) {
      const startDate = new Date(filters.year, filters.month, 1).toISOString().split('T')[0];
      const endDate = new Date(filters.year, filters.month + 1, 0).toISOString().split('T')[0];
      query = query.gte('date', startDate).lte('date', endDate);
    }

    // Filtre par période (dates spécifiques)
    if (filters.startDate) {
      query = query.gte('date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('date', filters.endDate);
    }

      const { data: dataDb, error: transactionsError } = await query.order('date', { ascending: false });
      if (transactionsError) {
        console.error('Erreur lors de la récupération des transactions filtrées:', transactionsError);
        return { data: null, error: transactionsError };
      }
      transactionsData = dataDb || [];
    }

    if (!transactionsData || transactionsData.length === 0) {
      return { data: [], error: null };
    }

    // Recherche côté client
    let filteredTransactions = transactionsData;
    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const searchTerm = filters.searchQuery.toLowerCase().trim();
      filteredTransactions = transactionsData.filter(transaction =>
        (transaction.description && transaction.description.toLowerCase().includes(searchTerm))
      );
    }

    // Si on est en impersonation, les catégories/comptes sont inclus dans les objets JSON renvoyés par la RPC
    // Éviter d'appeler des tables protégées par RLS
    let enrichedTransactions: TransactionWithDetails[];
    if (impersonatedUserId) {
      enrichedTransactions = filteredTransactions.map((transaction: any) => {
        const correctedTransaction: any = { ...transaction };
        if (!correctedTransaction.type) {
          correctedTransaction.type = correctedTransaction.montant < 0 ? 'depense' : 'revenu';
        }
        if (correctedTransaction.type === 'depense' && correctedTransaction.montant > 0) {
          correctedTransaction.montant = -correctedTransaction.montant;
        }
        if (correctedTransaction.type === 'revenu' && correctedTransaction.montant < 0) {
          correctedTransaction.montant = Math.abs(correctedTransaction.montant);
        }
        return {
          ...correctedTransaction,
          categorie: transaction.categorie || null,
          compte: transaction.compte || null,
          compte_destination: transaction.compte_destination || null,
        } as TransactionWithDetails;
      });
    } else {
      // Récupérer les catégories pour toutes les transactions filtrées
      const categorieIds = Array.from(new Set(filteredTransactions.map((t: any) => t.categorie_id)));
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .in('id', categorieIds);
      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
      }
      // Récupérer les comptes pour toutes les transactions filtrées
      const compteIds = Array.from(new Set(filteredTransactions.map((t: any) => t.compte_id)));
      const compteDestinationIds = Array.from(new Set(filteredTransactions
        .filter((t: any) => t.compte_destination_id)
        .map((t: any) => t.compte_destination_id)));
      const allCompteIds = Array.from(new Set([...compteIds, ...compteDestinationIds.filter((id: any) => id !== null)]));
      const { data: comptesData, error: comptesError } = await supabase
        .from('comptes')
        .select('*')
        .in('id', allCompteIds);
      if (comptesError) {
        console.error('Erreur lors de la récupération des comptes:', comptesError);
      }
      enrichedTransactions = filteredTransactions.map((transaction: any) => {
        const categorie = categoriesData?.find((cat: any) => cat.id === transaction.categorie_id);
        const compte = comptesData?.find((cpt: any) => cpt.id === transaction.compte_id);
        const compteDestination = transaction.compte_destination_id ? 
          comptesData?.find((cpt: any) => cpt.id === transaction.compte_destination_id) : 
          null;
        const correctedTransaction: any = { ...transaction };
        if (!correctedTransaction.type) {
          correctedTransaction.type = correctedTransaction.montant < 0 ? 'depense' : 'revenu';
        }
        if (correctedTransaction.type === 'depense' && correctedTransaction.montant > 0) {
          correctedTransaction.montant = -correctedTransaction.montant;
        }
        if (correctedTransaction.type === 'revenu' && correctedTransaction.montant < 0) {
          correctedTransaction.montant = Math.abs(correctedTransaction.montant);
        }
        return {
          ...correctedTransaction,
          categorie: categorie || null,
          compte: compte || null,
          compte_destination: compteDestination
        } as TransactionWithDetails;
      });
    }

    return { data: enrichedTransactions, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération des transactions filtrées:', error);
    return { data: null, error };
  }
};

/**
 * Met à jour une transaction existante
 */
export const updateTransaction = async (id: string, updates: Partial<Transaction>): Promise<{ data: Transaction | null; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour de la transaction:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la mise à jour de la transaction:', error);
    return { data: null, error };
  }
};

/**
 * Supprime une transaction existante
 */
export const deleteTransaction = async (id: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erreur lors de la suppression de la transaction:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception lors de la suppression de la transaction:', error);
    return { success: false, error };
  }
};

/**
 * Recherche globale dans toutes les transactions
 */
export const searchGlobalTransactions = async (
  filters: GlobalSearchFilters
): Promise<{ data: GlobalSearchResult | null; error: any }> => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId2 = getImpersonatedUserIdFromStorage();
    const userId = impersonatedUserId2 || sessionData.session.user.id;
    
    // Paramètres de pagination
    const page = filters.page || 1;
    const limit = filters.limit || 20;
    const offset = (page - 1) * limit;
    
    // Construction de la requête de base
    let query = supabase
      .from('transactions')
      .select(`
        *,
        categorie:categories(id, nom, couleur, icone, type),
        compte:comptes!transactions_compte_id_fkey(id, nom, couleur),
        compte_destination:comptes!transactions_compte_destination_id_fkey(id, nom, couleur)
      `, { count: 'exact' })
      .eq('user_id', userId);
    
    // Filtre de recherche textuelle
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const searchTerm = filters.searchQuery.trim();
      query = query.ilike('description', `%${searchTerm}%`);
    }
    
    // Filtre par type
    if (filters.type && filters.type !== 'all') {
      query = query.eq('type', filters.type);
    }
    
    // Filtre par compte
    if (filters.compte_id) {
      query = query.eq('compte_id', filters.compte_id);
    }
    
    // Filtre par catégorie
    if (filters.categorie_id) {
      query = query.eq('categorie_id', filters.categorie_id);
    }
    
    // Filtre par période
    if (filters.startDate) {
      query = query.gte('date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('date', filters.endDate);
    }
    
    // Pagination
    query = query.range(offset, offset + limit - 1);
    
    const { data: transactions, error, count } = await query.order('date', { ascending: false });
    
    if (error) {
      console.error('Erreur lors de la recherche globale:', error);
      return { data: null, error };
    }
    
    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / limit);
    
    const result: GlobalSearchResult = {
      transactions: transactions || [],
      totalCount,
      currentPage: page,
      totalPages
    };
    
    return { data: result, error: null };
  } catch (error) {
    console.error('Exception lors de la recherche globale:', error);
    return { data: null, error };
  }
};

/**
 * Récupère les transactions pour une période donnée
 */
export const getTransactions = async (startDate: string, endDate: string): Promise<{ data: TransactionWithDetails[] | null; error: any }> => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const impersonatedUserId3 = getImpersonatedUserIdFromStorage();
    
    // Récupérer les transactions de la période avec les détails des catégories et comptes
    const { data: transactions, error } = await supabase
      .from('transactions')
      .select(`
        *,
        categorie:categories!transactions_categorie_id_fkey(nom, couleur),
        compte:comptes!transactions_compte_id_fkey(nom, couleur),
        compte_destination:comptes!transactions_compte_destination_id_fkey(nom, couleur)
      `)
      .eq('user_id', impersonatedUserId3 || sessionData.session.user.id)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });
      
    if (error) {
      console.error('Erreur lors de la récupération des transactions:', error);
      return { data: null, error };
    }
    
    return { data: transactions, error: null };
  } catch (error) {
    console.error('Exception dans getTransactions:', error);
    return { data: null, error };
  }
}; 